import { sample } from 'effector';
import notification from 'antd/es/notification';
import i18 from '../../i18n';
import {
  $authStatus,
  $editProfileStatus,
  $globalPassChangeModal,
  $globalPassChangeSuccess,
  $language,
  $profileActiveTab,
  $regRequestSuccess,
  $regSuccess,
  $servicesActiveTab,
  $signedCert,
  $userEditPerms,
  $userInfo,
  $verificationStatus,
} from './stores.js';
import {
  authorizeDSFx,
  changePasswordFx,
  completeRegFx,
  confirmUserAgreementFx,
  editProfileFx,
  getCurrentUserFx,
  getRegInfoFx,
  loginFx,
  logoutFx,
  payOCHVFx,
  submitInvestorRegFx,
  verifyEmailFx,
} from './effects.js';
import {
  authorizeDSEv,
  changeLanguageEv,
  changePasswordEv,
  changeProfileActiveTabEv,
  changeServiceActiveTabEv,
  completeRegEv,
  confirmUserAgreementEv,
  editProfileEv,
  loginEv,
  logoutEv,
  payOCHVEv,
  RegisterGate,
  RegRequestGate,
  resetEditProfileStatusEv,
  setAuthStatusEv,
  setGlobalPassChangeModalEv,
  submitInvestorRegEv,
  VerifyGate,
} from './events.js';
import { cookies } from '../../api/axiosInstance.js';
import { prepareEditPerms } from '../../counters/auth/index.js';

$authStatus
  .on(loginFx.doneData, () => true)
  .on(setAuthStatusEv, (state, payload) => payload);

$signedCert
  .on(authorizeDSFx.doneData, (_, payload) => {
    const resp = typeof payload === 'string' ? payload : payload[0];
    return resp.replace('/[\\n\\r]/gm', '').split('-----')[2].trim();
  })
  .reset(RegisterGate.close);

$regSuccess.on(completeRegFx.doneData, () => true).reset(RegisterGate.close);

$regRequestSuccess
  .on(submitInvestorRegFx.doneData, () => true)
  .reset(RegRequestGate.close);

$verificationStatus
  .on(verifyEmailFx.doneData, () => true)
  .reset(VerifyGate.close);

$editProfileStatus
  .on(editProfileFx.doneData, () => true)
  .reset(resetEditProfileStatusEv);

$globalPassChangeModal
  .on(setGlobalPassChangeModalEv, (state, payload) => payload)
  .reset($globalPassChangeSuccess.updates);

$globalPassChangeSuccess.on(changePasswordFx.doneData, () => true);

authorizeDSFx.doneData.watch(
  (ev) =>
    ev &&
    notification.success({
      message: 'Успешно',
      description: 'Авторизация ЭЦП завершена',
    })
);

completeRegFx.doneData.watch(
  (ev) =>
    ev &&
    notification.success({
      message: 'Успешно',
      description:
        'Регистрация завершена. Авторизуйтесь с помощью вашего БИН и пароля, указанного при регистрации.',
      duration: 5,
    })
);

sample({
  clock: loginEv,
  target: loginFx,
});

loginFx.doneData.watch((data) => {
  if (window.location.host.includes('localhost')) {
    cookies.set('access_token', data.access_token);
  }
});

sample({
  clock: logoutEv,
  target: logoutFx,
});

sample({
  clock: logoutFx.doneData,
  fn: () => {
    cookies.remove('access_token');
    cookies.remove('refresh_token');
    window.location.href = '/login';
    return false;
  },
  target: setAuthStatusEv,
});

sample({
  clock: authorizeDSEv,
  target: authorizeDSFx,
});

sample({
  clock: completeRegEv,
  target: completeRegFx,
});

sample({
  clock: getCurrentUserFx.doneData,
  target: $userInfo,
});

sample({
  clock: getCurrentUserFx.doneData,
  fn: (clock) => prepareEditPerms(clock.role),
  target: $userEditPerms,
});

sample({
  clock: RegRequestGate.open,
  target: getRegInfoFx,
});

sample({
  clock: submitInvestorRegEv,
  target: submitInvestorRegFx,
});

sample({
  clock: changeLanguageEv,
  fn: (lang) => {
    localStorage.setItem('lang', lang);
    i18.changeLanguage(lang).then(() => true);
    return lang;
  },
  target: $language,
});

sample({
  clock: VerifyGate.state.updates,
  filter: (gate) => !!gate.token,
  fn: (gate) => gate.token,
  target: verifyEmailFx,
});

sample({
  clock: changePasswordEv,
  target: changePasswordFx,
});

sample({
  clock: editProfileEv,
  target: editProfileFx,
});

sample({
  clock: [
    changePasswordFx.doneData,
    editProfileFx.doneData,
    confirmUserAgreementFx.doneData,
  ],
  target: getCurrentUserFx,
});

changePasswordFx.doneData.watch(
  (ev) =>
    ev &&
    notification.success({ message: 'Успешно!', description: 'Пароль изменен' })
);

editProfileFx.doneData.watch(
  (ev) =>
    ev &&
    notification.success({
      message: 'Успешно!',
      description: 'Личные данные обновлены',
    })
);

sample({
  clock: changeProfileActiveTabEv,
  target: $profileActiveTab,
});

sample({
  clock: changeServiceActiveTabEv,
  target: $servicesActiveTab,
});

sample({
  clock: payOCHVEv,
  target: payOCHVFx,
});

sample({
  clock: payOCHVFx.doneData,
  fn: (clock) => {
    halyk.pay({
      ...clock.payment_object,
      auth: clock.auth,
    });
  },
});

sample({
  clock: confirmUserAgreementEv,
  target: confirmUserAgreementFx,
});
